import { useEffect, useRef, useMemo, useCallback } from 'react';
import { StructureCategory, FarmRiskType, lookupsJson } from '@ourbranch/lookups';
import { brandConfig } from 'brand-config';
import { validate80PercentRC, validate80PercentACV } from './FO6.utils';

/**
 * Helper function to get building class 1 maximum age from brand configuration
 * @param {string} state - State code (e.g., 'IL', 'MO')
 * @returns {number} Maximum age in years for Building Class 1 (defaults to 25)
 */
const getBuildingClass1MaxAge = (state = 'IL') => {
  return brandConfig?.farm?.[state]?.[FarmRiskType.Fo6Structures]?.businessRules?.buildingClass1MaxAgeYears ?? 25;
};

/**
 * Custom hook to consolidate FO6 business logic and useEffect hooks
 * Manages building type auto-setting, building class enforcement, and coverage tracking
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.item - Risk item data
 * @param {string} params.fieldName - Formik field name prefix
 * @param {Function} params.setFieldValue - Formik setFieldValue function
 * @param {string} params.buildingType - Building type (structure category)
 * @param {number} params.initialCoverageE - Initial coverage E value
 * @param {string} params.riskType - Risk type identifier
 * @param {string} params.state - State code (e.g., 'IL', 'MO')
 * @param {Object} params.values - Current form values from Formik
 * @returns {Object} Business logic values and handlers
 */
export const useFO6BusinessLogic = ({
  item,
  fieldName,
  setFieldValue,
  buildingType,
  initialCoverageE,
  riskType,
  state,
  values
}) => {
  // Coverage update tracking
  const coverageHasBeenUpdatedRef = useRef(false);

  // Track previous buildingType to detect changes
  const prevBuildingTypeRef = useRef(null);

  // Determine structure type flags
  const isBarnOutbuilding = buildingType === StructureCategory.BarnsOutbuildings;
  const isPoultryBuilding = buildingType === StructureCategory.PoultryBuildings;
  const isDwellingContents = buildingType === StructureCategory.DwellingContents;
  const isFencing = buildingType === StructureCategory.Fencing;
  const isConfinementBuilding = buildingType === StructureCategory.ConfinementBuildings;

  // Occupancy-based flags
  const isOpenSidedBuilding = item.building?.occupancy === 'OSB';
  const isQuonsetBuilding = item.building?.occupancy === 'QHB';
  const isHoopBuilding = buildingType === StructureCategory.HoopBuildings;

  // Get building class for barn business logic (check current form values first)
  const currentBuilding = values?.[fieldName]?.building || item.building || {};
  const buildingClass = currentBuilding.buildingClass;

  // Required field logic based on building class
  const isRequiredFieldForClass = useCallback(
    (fieldName) => {
      // Confinement/Poultry Building RC: Require Special Form + Snow Weight + Replacement Cost Provision
      if ((isConfinementBuilding || isPoultryBuilding) && item.coverages?.structureLossSettlement === 'RC') {
        return ['specialFormCoverage', 'hasSnowWeightCoverage', 'hasReplacementCostCoverage'].includes(fieldName);
      }

      if (!isBarnOutbuilding) {
        return false;
      }
      // Building Class 1: Require RC + Special Form + Snow Weight + Replacement Cost Provision
      if (buildingClass === 1) {
        return [
          'structureLossSettlement',
          'specialFormCoverage',
          'hasSnowWeightCoverage',
          'hasReplacementCostCoverage'
        ].includes(fieldName);
      }
      // Building Class 2: Require RC + Special Form + Snow Weight + Replacement Cost Provision
      if (buildingClass === 2) {
        return [
          'structureLossSettlement',
          'specialFormCoverage',
          'hasSnowWeightCoverage',
          'hasReplacementCostCoverage'
        ].includes(fieldName);
      }
      // Building Class 3: Lock RC + Special Form + Snow Weight to specific values
      if (buildingClass === 3) {
        return ['structureLossSettlement', 'specialFormCoverage', 'hasSnowWeightCoverage'].includes(fieldName);
      }
      return false;
    },
    [
      isConfinementBuilding,
      isPoultryBuilding,
      item.coverages?.structureLossSettlement,
      isBarnOutbuilding,
      buildingClass
    ]
  );

  // Add Building Class 1 eligibility validation
  const type1EligibilityValidation = useMemo(() => {
    if (!isBarnOutbuilding) {
      return { isValid: true, message: '' };
    }

    const currentYear = new Date().getFullYear();
    // Check current form values first, fall back to initial item values
    const currentBuilding = values?.[fieldName]?.building || item.building || {};
    const buildingAge = currentBuilding.yearBuilt ? currentYear - currentBuilding.yearBuilt : null;
    const hasOpenSides = currentBuilding.structureOpenSides;
    const maxAge = getBuildingClass1MaxAge(state);

    // Building Class 1 eligibility violations
    if (buildingAge !== null && buildingAge > maxAge) {
      return {
        isValid: false,
        field: 'yearBuilt',
        message: `Building age (${buildingAge} years) exceeds Building Class 1 maximum (${maxAge} years). Must use Building Class 2 or 3.`
      };
    }

    if (hasOpenSides) {
      return {
        isValid: false,
        field: 'structureOpenSides',
        message: 'Building Class 1 barns cannot have open sides. Must use Building Class 2 or 3.'
      };
    }

    return { isValid: true, message: '' };
  }, [
    isBarnOutbuilding,
    values?.[fieldName]?.building?.yearBuilt,
    values?.[fieldName]?.building?.structureOpenSides,
    item.building?.yearBuilt,
    item.building?.structureOpenSides,
    state
  ]);

  // Available building class options (filtered for barns that don't meet Class 1 requirements)
  const availableBuildingClassOptions = useMemo(() => {
    const buildingClassOptions = lookupsJson.farm.buildingClassOptions;

    if (!isBarnOutbuilding || type1EligibilityValidation.isValid) {
      return buildingClassOptions;
    }

    // Filter out Class 1 when requirements not met
    return buildingClassOptions.filter((option) => option.id !== 1);
  }, [isBarnOutbuilding, type1EligibilityValidation.isValid]);

  // Validation and options
  const rcValidation = useMemo(() => {
    return validate80PercentRC({
      coverages: item.coverages,
      building: item.building,
      isNewRisk: !item.id // Pass hasBeenRated context
    });
  }, [item.building, item.coverages, item.id]);

  // Effect 1: Poultry building occupancy auto-setting
  useEffect(() => {
    if (isPoultryBuilding) {
      setFieldValue(`${fieldName}.building.occupancy`, 'PCH');
    }
  }, [isPoultryBuilding, setFieldValue, fieldName]);

  // Effect 2: Open Sided Building logic
  useEffect(() => {
    if (isOpenSidedBuilding) {
      // Open Sided Building: Auto-check "Has open sides?" and disable it
      setFieldValue(`${fieldName}.building.structureOpenSides`, true);
    }
  }, [isOpenSidedBuilding, setFieldValue, fieldName]);

  // Effect 3: Quonset Building roof type setting
  useEffect(() => {
    if (isQuonsetBuilding) {
      // Quonset Building: Set roof type to special value for included construction
      setFieldValue(`${fieldName}.building.roofType`, 'NONEQ');
    }
  }, [isQuonsetBuilding, setFieldValue, fieldName]);

  // Effect 4: Hoop Building roof type setting
  useEffect(() => {
    if (isHoopBuilding) {
      // Hoop Building: Set roof type to indicate included in construction (consistent with Quonset)
      setFieldValue(`${fieldName}.building.roofType`, 'NONEH');
      // Note: Hoop buildings automatically get 'HP' occupancy use in backend processing
    }
  }, [isHoopBuilding, setFieldValue, fieldName]);

  // Effect 5: Building Class Business Rules Enforcement
  useEffect(() => {
    if (!isBarnOutbuilding || !buildingClass) {
      return;
    }

    // Building Class 1 & 2: Must use RC
    if (buildingClass === 1 || buildingClass === 2) {
      if (item.coverages?.structureLossSettlement !== 'RC') {
        setFieldValue(`${fieldName}.coverages.structureLossSettlement`, 'RC');
      }
      // Also enforce that special form and snow weight are enabled for class 1 & 2
      if (!item.coverages?.specialFormCoverage) {
        setFieldValue(`${fieldName}.coverages.specialFormCoverage`, true);
      }
      if (!item.coverages?.hasSnowWeightCoverage) {
        setFieldValue(`${fieldName}.coverages.hasSnowWeightCoverage`, true);
      }
      // Roof loss settlement for Building Class 1 & 2 is handled by Effect 7 (universal enforcer)
      // GitHub issue #29142
    }

    // Building Class 1 & 2: Must have Replacement Cost Provision (when structure loss settlement is forced to RC)
    if (buildingClass === 1 || buildingClass === 2) {
      if (!item.coverages?.hasReplacementCostCoverage) {
        setFieldValue(`${fieldName}.coverages.hasReplacementCostCoverage`, true);
      }
    }

    // Building Class 3: Must use ACV
    if (buildingClass === 3) {
      if (item.coverages?.structureLossSettlement !== 'ACV') {
        setFieldValue(`${fieldName}.coverages.structureLossSettlement`, 'ACV');
      }
      // Also enforce that special form and snow weight are disabled for class 3
      if (item.coverages?.specialFormCoverage) {
        setFieldValue(`${fieldName}.coverages.specialFormCoverage`, false);
      }
      if (item.coverages?.hasSnowWeightCoverage) {
        setFieldValue(`${fieldName}.coverages.hasSnowWeightCoverage`, false);
      }
      if (item.coverages?.hasReplacementCostCoverage) {
        setFieldValue(`${fieldName}.coverages.hasReplacementCostCoverage`, false);
      }
      // Set roof loss settlement to ACV for Building Class 3
      if (item.coverages?.roofLossSettlement !== 'ACV') {
        setFieldValue(`${fieldName}.coverages.roofLossSettlement`, 'ACV');
      }
    }
  }, [
    isBarnOutbuilding,
    buildingClass,
    item.coverages?.structureLossSettlement,
    item.coverages?.specialFormCoverage,
    item.coverages?.hasSnowWeightCoverage,
    item.coverages?.hasReplacementCostCoverage,
    setFieldValue,
    fieldName
  ]);

  // Effect 6: Auto-enable cosmetic damage exclusion and all three types for all FO6 structures
  useEffect(() => {
    if (riskType === FarmRiskType.Fo6Structures) {
      // Only set values if they're different to prevent dirty state
      if (item.coverages?.cosmeticDamageExclusion !== true) {
        setFieldValue(`${fieldName}.coverages.cosmeticDamageExclusion`, true);
      }
      if (item.coverages?.exteriorDoorsWindowsDamageExclusion !== true) {
        setFieldValue(`${fieldName}.coverages.exteriorDoorsWindowsDamageExclusion`, true);
      }
      if (item.coverages?.exteriorWallsDamageExclusion !== true) {
        setFieldValue(`${fieldName}.coverages.exteriorWallsDamageExclusion`, true);
      }
      if (item.coverages?.roofsDamageExclusion !== true) {
        setFieldValue(`${fieldName}.coverages.roofsDamageExclusion`, true);
      }
    }
  }, [
    riskType,
    item.coverages?.cosmeticDamageExclusion,
    item.coverages?.exteriorDoorsWindowsDamageExclusion,
    item.coverages?.exteriorWallsDamageExclusion,
    item.coverages?.roofsDamageExclusion,
    setFieldValue,
    fieldName
  ]);

  // Effect 7: Universal Roof Loss Settlement Enforcement
  // GitHub issue #29142: Structure=RC → Roof=RC_ACV_WIND_HAIL, Structure=ACV → Roof=ACV
  // unless exclusion/restriction overrides (handled by Effect 10)
  useEffect(() => {
    // When roof exclusion/restriction is active, defer to Effect 10 for roof value
    if (item.coverages?.hasRoofExclusionRestriction) {
      return;
    }

    // Barn class 3 always ACV
    if (isBarnOutbuilding && buildingClass === 3 && item.coverages?.roofLossSettlement !== 'ACV') {
      setFieldValue(`${fieldName}.coverages.roofLossSettlement`, 'ACV');
      return;
    }

    // Structure = RC → Roof = RC_ACV_WIND_HAIL
    if (item.coverages?.structureLossSettlement === 'RC' && item.coverages?.roofLossSettlement !== 'RC_ACV_WIND_HAIL') {
      setFieldValue(`${fieldName}.coverages.roofLossSettlement`, 'RC_ACV_WIND_HAIL');
      return;
    }

    // Structure = ACV → Roof = ACV
    if (item.coverages?.structureLossSettlement === 'ACV' && item.coverages?.roofLossSettlement !== 'ACV') {
      setFieldValue(`${fieldName}.coverages.roofLossSettlement`, 'ACV');
    }
  }, [
    item.coverages?.structureLossSettlement,
    item.coverages?.roofLossSettlement,
    item.coverages?.hasRoofExclusionRestriction,
    buildingClass,
    isBarnOutbuilding,
    setFieldValue,
    fieldName
  ]);

  // Effect 8: Coverage update tracking
  useEffect(() => {
    coverageHasBeenUpdatedRef.current = initialCoverageE !== item.coverages?.coverageE;
  }, [initialCoverageE, item.coverages?.coverageE]);

  // Effect 9: Confinement Building RC Business Rules
  // When RC building loss settlement is selected, auto-enable and lock:
  // - Special form coverage
  // - Replacement cost provision
  // - Weight of ice, snow or sleet coverage
  useEffect(() => {
    if (!isConfinementBuilding) {
      return;
    }

    if (item.coverages?.structureLossSettlement === 'RC') {
      // Auto-enable required coverages
      if (!item.coverages?.specialFormCoverage) {
        setFieldValue(`${fieldName}.coverages.specialFormCoverage`, true);
      }
      if (!item.coverages?.hasReplacementCostCoverage) {
        setFieldValue(`${fieldName}.coverages.hasReplacementCostCoverage`, true);
      }
      if (!item.coverages?.hasSnowWeightCoverage) {
        setFieldValue(`${fieldName}.coverages.hasSnowWeightCoverage`, true);
      }
      // Roof loss settlement is handled by Effect 7 (universal enforcer) - GitHub issue #29142
    }
  }, [
    isConfinementBuilding,
    item.coverages?.structureLossSettlement,
    item.coverages?.specialFormCoverage,
    item.coverages?.hasReplacementCostCoverage,
    item.coverages?.hasSnowWeightCoverage,
    setFieldValue,
    fieldName
  ]);

  /**
   * Effect 10: Roof Exclusion/Restriction sync with Roof Loss Settlement
   *
   * Business Rules:
   * - 'excluded': Roof excluded from coverage → roofLossSettlement = 'NONE'
   * - 'ACV': Roof covered at actual cash value → roofLossSettlement = 'ACV'
   * - Other values: Defer to existing business logic from Effects 5 & 7
   *
   * This syncs the FO6-specific roofExclusionRestriction field with roofLossSettlement,
   * matching FO1/FO2/FO3 behavior where roof exclusion automatically adjusts loss
   * settlement options. Guards prevent infinite loops.
   */
  useEffect(() => {
    const roofExclusionRestriction = item.coverages?.roofExclusionRestriction;
    const currentRoofLoss = item.coverages?.roofLossSettlement;
    const hasRestrictionToggle = item.coverages?.hasRoofExclusionRestriction;

    // When the toggle is off, clean up any residual state from a previously active restriction.
    // This ensures roofLossSettlement reverts from NONE and roofExclusionRestriction is cleared. (#29139)
    if (!hasRestrictionToggle) {
      if (currentRoofLoss === 'NONE') {
        // Barn Class 1/2 defaults to RC_ACV_WIND_HAIL; all other types default to ACV (#29139)
        const revertValue =
          isBarnOutbuilding && (buildingClass === 1 || buildingClass === 2) ? 'RC_ACV_WIND_HAIL' : 'ACV';
        setFieldValue(`${fieldName}.coverages.roofLossSettlement`, revertValue);
      }
      if (roofExclusionRestriction && roofExclusionRestriction !== 'none') {
        setFieldValue(`${fieldName}.coverages.roofExclusionRestriction`, 'none');
      }
      return;
    }

    // Building Class 3 barns must use ACV - don't override with NONE
    if (isBarnOutbuilding && buildingClass === 3) {
      return;
    }

    if (roofExclusionRestriction === 'excluded') {
      // Roof Excluded → set roof loss settlement to NONE
      if (currentRoofLoss !== 'NONE') {
        setFieldValue(`${fieldName}.coverages.roofLossSettlement`, 'NONE');
      }
    } else if (roofExclusionRestriction === 'ACV') {
      // Roof Actual Cash Value → set roof loss settlement to ACV
      if (currentRoofLoss !== 'ACV') {
        setFieldValue(`${fieldName}.coverages.roofLossSettlement`, 'ACV');
      }
    }
    // If roofExclusionRestriction is undefined or any other value, don't override
    // (let existing business logic from other effects handle defaults)
  }, [
    item.coverages?.hasRoofExclusionRestriction,
    item.coverages?.roofExclusionRestriction,
    item.coverages?.roofLossSettlement,
    isBarnOutbuilding,
    buildingClass,
    setFieldValue,
    fieldName
  ]);

  // Effect 11: Poultry Building RC Business Rules
  // When RC building loss settlement is selected, auto-enable and lock:
  // - Special form coverage
  // - Replacement cost provision
  // - Weight of ice, snow or sleet coverage
  // (Same requirements as confinement buildings - see Effect 9)
  useEffect(() => {
    if (!isPoultryBuilding) {
      return;
    }

    if (item.coverages?.structureLossSettlement === 'RC') {
      // Auto-enable required coverages
      if (!item.coverages?.specialFormCoverage) {
        setFieldValue(`${fieldName}.coverages.specialFormCoverage`, true);
      }
      if (!item.coverages?.hasReplacementCostCoverage) {
        setFieldValue(`${fieldName}.coverages.hasReplacementCostCoverage`, true);
      }
      if (!item.coverages?.hasSnowWeightCoverage) {
        setFieldValue(`${fieldName}.coverages.hasSnowWeightCoverage`, true);
      }
      // Roof loss settlement is handled by Effect 7 (universal enforcer) - GitHub issue #29142
    }
  }, [
    isPoultryBuilding,
    item.coverages?.structureLossSettlement,
    item.coverages?.specialFormCoverage,
    item.coverages?.hasReplacementCostCoverage,
    item.coverages?.hasSnowWeightCoverage,
    setFieldValue,
    fieldName
  ]);

  // Effect 12: Default contentsLimit to 0 for Dwelling/Contents structures
  useEffect(() => {
    // Only default for new (unsaved) risks to avoid dirtying existing policies on load
    if (isDwellingContents && item.coverages?.contentsLimit == null && !item.id) {
      setFieldValue(`${fieldName}.coverages.contentsLimit`, 0);
    }
  }, [isDwellingContents, item.coverages?.contentsLimit, item.id, setFieldValue, fieldName]);

  // Effect 13: ACV Structure Loss Settlement — force optional coverages off
  // When structureLossSettlement is ACV for non-barn building types (grain bins, hoop, fencing,
  // confinement, poultry), disable Special Form, Replacement Cost Provision, and Snow/Ice coverage.
  // Barn Class 3 ACV lock-off is already handled by Effect 5.
  // Related issues: #28952, #28951, #28949, #28948, #28944
  useEffect(() => {
    if (item.coverages?.structureLossSettlement !== 'ACV') {
      return;
    }
    // Skip for barns — Effect 5 already handles Class 3 ACV lock-off
    if (isBarnOutbuilding) {
      return;
    }

    if (item.coverages?.specialFormCoverage) {
      setFieldValue(`${fieldName}.coverages.specialFormCoverage`, false);
    }
    if (item.coverages?.hasReplacementCostCoverage) {
      setFieldValue(`${fieldName}.coverages.hasReplacementCostCoverage`, false);
    }
    if (item.coverages?.hasSnowWeightCoverage) {
      setFieldValue(`${fieldName}.coverages.hasSnowWeightCoverage`, false);
    }
  }, [
    item.coverages?.structureLossSettlement,
    item.coverages?.specialFormCoverage,
    item.coverages?.hasReplacementCostCoverage,
    item.coverages?.hasSnowWeightCoverage,
    isBarnOutbuilding,
    setFieldValue,
    fieldName
  ]);

  // Effect 14: Auto-fill coverageE when structureLossSettlement changes for poultry/confinement buildings
  // For ACV: set coverageE to ACV total value
  // For RC: set coverageE to 100% of RCE total value
  // Related issue: #29138
  // Guards:
  //   1. coverageHasBeenUpdatedRef: skip if user has manually customised coverageE
  //   2. current-value equality: skip if coverageE already holds the target value (avoids form dirty on load)
  useEffect(() => {
    if (!isPoultryBuilding && !isConfinementBuilding) {
      return;
    }

    // Don't overwrite a value the user has manually set (tracked by Effect 8)
    if (coverageHasBeenUpdatedRef.current) {
      return;
    }

    const lossSettlement = item.coverages?.structureLossSettlement;
    const acvTotal = item.building?.actualCashValueCalculation?.total;
    const rceTotal = item.building?.replacementCostEstimate?.total;
    const currentCoverageE = item.coverages?.coverageE;

    if (lossSettlement === 'ACV' && acvTotal && currentCoverageE !== acvTotal) {
      setFieldValue(`${fieldName}.coverages.coverageE`, acvTotal);
    } else if (lossSettlement === 'RC' && rceTotal && currentCoverageE !== rceTotal) {
      setFieldValue(`${fieldName}.coverages.coverageE`, rceTotal);
    }
  }, [
    isPoultryBuilding,
    isConfinementBuilding,
    item.coverages?.structureLossSettlement,
    item.coverages?.coverageE,
    item.building?.actualCashValueCalculation?.total,
    item.building?.replacementCostEstimate?.total,
    setFieldValue,
    fieldName
  ]);

  // Effect 15: Default primaryHeatingSource to 'NONE' for agricultural structure categories
  // BO (Barns/Outbuildings), C (Confinement), H (Hoop), PO (Poultry) should default to no heating
  // The field remains required — this default satisfies the requirement so users don't have to fill it manually
  // GitHub issue #29144
  // Null guard removed: always reset to 'NONE' when buildingType changes to Poultry to clear stale values
  useEffect(() => {
    const prevBuildingType = prevBuildingTypeRef.current;
    prevBuildingTypeRef.current = buildingType;

    if (isBarnOutbuilding || isConfinementBuilding || isHoopBuilding || isPoultryBuilding) {
      // Reset whenever transitioning to Poultry (clears stale values) or when no value is set
      if (isPoultryBuilding && prevBuildingType !== buildingType) {
        setFieldValue(`${fieldName}.building.primaryHeatingSource`, 'NONE');
      } else if (item.building?.primaryHeatingSource == null) {
        setFieldValue(`${fieldName}.building.primaryHeatingSource`, 'NONE');
      }
    }
  }, [
    isBarnOutbuilding,
    isConfinementBuilding,
    isHoopBuilding,
    isPoultryBuilding,
    buildingType,
    item.building?.primaryHeatingSource,
    setFieldValue,
    fieldName
  ]);

  // ACV validation (mirrors RC validation for ACV buildings)
  const acvValidation = useMemo(
    () =>
      validate80PercentACV({
        coverages: item.coverages,
        building: item.building,
        isNewRisk: !item.id
      }),
    [item.building, item.coverages, item.id]
  );

  // Select the appropriate validation based on loss settlement type
  const coverageValidation = item.coverages?.structureLossSettlement === 'ACV' ? acvValidation : rcValidation;

  // Return all computed values and refs needed by the component
  return {
    // Structure type flags
    isBarnOutbuilding,
    isPoultryBuilding,
    isDwellingContents,
    isFencing,
    isConfinementBuilding,
    isOpenSidedBuilding,
    isQuonsetBuilding,
    isHoopBuilding,

    // Barn business logic
    buildingClass,
    isRequiredFieldForClass,
    type1EligibilityValidation,
    availableBuildingClassOptions,

    // Validation and options
    rcValidation,
    acvValidation,
    coverageValidation,

    // Refs
    coverageHasBeenUpdatedRef
  };
};
